/**
 * Support & Ticketing Module — Entity Types  (v5 — Progressive Resolution UX)
 * Priority REMOVED. Config-driven issue types & system areas. Brand-aggregator linking.
 */

// Enums

/** Which platform module the issue relates to. */
export enum IssueModule {
  AGGREGATOR = 'AGGREGATOR',
  MENU       = 'MENU',
}

/** Granular issue category. */
export enum IssueCategory {
  DISCOUNT     = 'DISCOUNT',
  IMAGE        = 'IMAGE',
  MISSING_ITEM = 'MISSING_ITEM',
  OFFERS       = 'OFFERS',
  OUT_OF_STOCK = 'OUT_OF_STOCK',
  PRICE        = 'PRICE',
  TIMING       = 'TIMING',
  DESCRIPTION  = 'DESCRIPTION',
  OTHER        = 'OTHER',
}

export enum TicketStatus {
  NEW         = 'NEW',
  OPEN        = 'OPEN',
  IN_PROGRESS = 'IN_PROGRESS',
  PENDING     = 'PENDING',
  RESOLVED    = 'RESOLVED',
  CLOSED      = 'CLOSED',
}

/** Per-issue resolution status. */
export enum IssueReportStatus {
  PENDING  = 'PENDING',
  RESOLVED = 'RESOLVED',
  REJECTED = 'REJECTED',
}

// Nested Entities

export interface IssueAttachment {
  id: string;
  name: string;
  type: string;
  size: number;
  data: string;
}

export interface IssueReport {
  id: string;
  module: IssueModule;
  brandIds: string[];
  aggregators: string[];
  category: IssueCategory;
  details: string;
  attachments: IssueAttachment[];
  status: IssueReportStatus;
  resolutionNote?: string;
}

// Status History (Lifecycle Tracking)

export interface StatusHistoryEntry {
  status: TicketStatus;
  timestamp: number;
  changedBy: string;
  changedByName: string;
}

// Chat / Discussion

export interface TicketMessage {
  id: string;
  ticketId: string;
  issueReportId?: string;
  senderId: string;
  senderName: string;
  senderRole: 'manager' | 'admin';
  content: string;
  mentions?: string[];
  createdAt: number;
}

// Ticket (Top-level) — NO priority field

export interface SlaTracker {
  createdAt: number;
  firstResponseAt?: number;
  resolvedAt?: number;
}

/** Resolution confirmation record — created when admin resolves a ticket */
export interface ResolutionRecord {
  resolvedBy: string;
  resolvedByName: string;
  resolvedAt: number;
  autoCloseHours: number;
  resolutionNote: string;
  autoCloseExpiry: number;
  /** Manager's response: 'confirmed' (closed), 'disputed' (reopened), or null (pending) */
  managerVerdict?: 'confirmed' | 'disputed' | null;
  verdictAt?: number;
  verdictMessage?: string;
  /** True when system auto-closed due to inactivity */
  autoClosedBySystem?: boolean;
}

export const AUTO_CLOSE_DURATIONS: { label: string; hours: 24 | 48 | 72 }[] = [
  { label: '24 Hours', hours: 24 },
  { label: '48 Hours', hours: 48 },
  { label: '72 Hours', hours: 72 },
];

export interface Ticket {
  id: string;
  subject: string;
  status: TicketStatus;
  managerId: string;
  managerName: string;
  orgId: string;
  issueReports: IssueReport[];
  assignedDepartmentId?: string;
  assignedUserId?: string;
  assignedUserName?: string;
  statusHistory: StatusHistoryEntry[];
  sla: SlaTracker;
  messages: TicketMessage[];
  createdAt: number;
  updatedAt: number;
  /** Auto-close expiry timestamp — set when admin resolves with a timer */
  autoCloseExpiry?: number;
  /** Full resolution confirmation history */
  resolutionHistory?: ResolutionRecord[];
}

// Admin-configurable support settings

export interface SupportDeskConfig {
  issueTypes: { id: string; label: string; icon: string }[];
  systemAreas: { id: string; label: string }[];
  slaTargets: { firstResponse: number; resolution: number };
  autoAssign: boolean;
  emailNotifications: boolean;
  criticalCategories: string[];
  autoCloseDurations: { label: string; hours: number }[];
}

export interface SupportSettings {
  autoAssignDepartment?: string;
  slaTargets: {
    firstResponse: number;
    resolution: number;
  };
  orgId?: string;
}

export interface KnowledgeArticle {
  id: string;
  title: string;
  content: string;
  category: IssueCategory;
  tags: string[];
  createdBy: string;
  createdAt: number;
  updatedAt: number;
  orgId?: string;
}

// Label / Color Maps

export const ISSUE_MODULE_LABELS: Record<IssueModule, string> = {
  [IssueModule.AGGREGATOR]: 'Aggregator Operations',
  [IssueModule.MENU]:       'Menu Management',
};

export const ISSUE_MODULE_REQUIRED_APP: Record<IssueModule, string> = {
  [IssueModule.AGGREGATOR]: 'aggregator',
  [IssueModule.MENU]:       'menu-manage',
};

export const ISSUE_CATEGORY_LABELS: Record<IssueCategory, string> = {
  [IssueCategory.DISCOUNT]:     'Discount',
  [IssueCategory.IMAGE]:        'Image',
  [IssueCategory.MISSING_ITEM]: 'Missing Item',
  [IssueCategory.OFFERS]:       'Offers',
  [IssueCategory.OUT_OF_STOCK]: 'Out of Stock',
  [IssueCategory.PRICE]:        'Price',
  [IssueCategory.TIMING]:       'Timing',
  [IssueCategory.DESCRIPTION]:  'Description',
  [IssueCategory.OTHER]:        'Other',
};

/** Lucide icon names for monochromatic rendering in the UI. */
export const ISSUE_CATEGORY_ICONS: Record<IssueCategory, string> = {
  [IssueCategory.DISCOUNT]:     'Tag',
  [IssueCategory.IMAGE]:        'Image',
  [IssueCategory.MISSING_ITEM]: 'AlertCircle',
  [IssueCategory.OFFERS]:       'Gift',
  [IssueCategory.OUT_OF_STOCK]: 'Package',
  [IssueCategory.PRICE]:        'DollarSign',
  [IssueCategory.TIMING]:       'Clock',
  [IssueCategory.DESCRIPTION]:  'FileText',
  [IssueCategory.OTHER]:        'Settings',
};

export const ISSUE_CATEGORY_COLORS: Record<IssueCategory, { bg: string; text: string; border: string }> = {
  [IssueCategory.DISCOUNT]:     { bg: 'bg-slate-50',  text: 'text-slate-700',  border: 'border-slate-200' },
  [IssueCategory.IMAGE]:        { bg: 'bg-slate-50',  text: 'text-slate-700',  border: 'border-slate-200' },
  [IssueCategory.MISSING_ITEM]: { bg: 'bg-slate-50',  text: 'text-slate-700',  border: 'border-slate-200' },
  [IssueCategory.OFFERS]:       { bg: 'bg-slate-50',  text: 'text-slate-700',  border: 'border-slate-200' },
  [IssueCategory.OUT_OF_STOCK]: { bg: 'bg-slate-50',  text: 'text-slate-700',  border: 'border-slate-200' },
  [IssueCategory.PRICE]:        { bg: 'bg-slate-50',  text: 'text-slate-700',  border: 'border-slate-200' },
  [IssueCategory.TIMING]:       { bg: 'bg-slate-50',  text: 'text-slate-700',  border: 'border-slate-200' },
  [IssueCategory.DESCRIPTION]:  { bg: 'bg-slate-50',  text: 'text-slate-700',  border: 'border-slate-200' },
  [IssueCategory.OTHER]:        { bg: 'bg-slate-50',  text: 'text-slate-600',  border: 'border-slate-200' },
};

export const TICKET_STATUS_LABELS: Record<TicketStatus, string> = {
  [TicketStatus.NEW]:         'New',
  [TicketStatus.OPEN]:        'Open',
  [TicketStatus.IN_PROGRESS]: 'In Progress',
  [TicketStatus.PENDING]:     'Pending',
  [TicketStatus.RESOLVED]:    'Resolved',
  [TicketStatus.CLOSED]:      'Closed',
};

export const STATUS_COLORS: Record<TicketStatus, { bg: string; text: string; dot: string }> = {
  [TicketStatus.NEW]:         { bg: 'bg-sky-100',     text: 'text-sky-700',     dot: 'bg-sky-500' },
  [TicketStatus.OPEN]:        { bg: 'bg-blue-100',    text: 'text-blue-700',    dot: 'bg-blue-600' },
  [TicketStatus.IN_PROGRESS]: { bg: 'bg-indigo-100',  text: 'text-indigo-700',  dot: 'bg-indigo-500' },
  [TicketStatus.PENDING]:     { bg: 'bg-orange-100',  text: 'text-orange-700',  dot: 'bg-orange-500' },
  [TicketStatus.RESOLVED]:    { bg: 'bg-emerald-100', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  [TicketStatus.CLOSED]:      { bg: 'bg-slate-100',   text: 'text-slate-500',   dot: 'bg-slate-400' },
};

/** Ordered status ladder for lifecycle tracking */
export const STATUS_LADDER: TicketStatus[] = [
  TicketStatus.NEW,
  TicketStatus.OPEN,
  TicketStatus.IN_PROGRESS,
  TicketStatus.PENDING,
  TicketStatus.RESOLVED,
  TicketStatus.CLOSED,
];

export const AGGREGATOR_PLATFORMS = ['Keeta', 'Rafeeq', 'Snoonu', 'Talabat', 'Deliveroo'] as const;

export const AGGREGATOR_COLORS: Record<string, { bg: string; text: string }> = {
  Keeta:     { bg: 'bg-emerald-50', text: 'text-emerald-700' },
  Rafeeq:    { bg: 'bg-sky-50',     text: 'text-sky-700' },
  Snoonu:    { bg: 'bg-orange-50',  text: 'text-orange-700' },
  Talabat:   { bg: 'bg-amber-50',   text: 'text-amber-700' },
  Deliveroo: { bg: 'bg-teal-50',    text: 'text-teal-700' },
};

export const MENTIONABLE_DEPARTMENTS = ['F&B', 'F&P', 'SALES', 'IT', 'MARKETING', 'OPS'] as const;

// Issue Report Status Maps

export const ISSUE_REPORT_STATUS_LABELS: Record<IssueReportStatus, string> = {
  [IssueReportStatus.PENDING]:  'Pending',
  [IssueReportStatus.RESOLVED]: 'Resolved',
  [IssueReportStatus.REJECTED]: 'Rejected',
};

export const ISSUE_REPORT_STATUS_COLORS: Record<IssueReportStatus, { bg: string; text: string; dot: string; border: string }> = {
  [IssueReportStatus.PENDING]:  { bg: 'bg-amber-50',   text: 'text-amber-700',   dot: 'bg-amber-400', border: 'border-amber-200' },
  [IssueReportStatus.RESOLVED]: { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500', border: 'border-emerald-200' },
  [IssueReportStatus.REJECTED]: { bg: 'bg-red-50',     text: 'text-red-700',     dot: 'bg-red-500', border: 'border-red-200' },
};

// Domain Logic

export function deriveTicketStatus(issueReports: IssueReport[], currentStatus: TicketStatus): TicketStatus {
  if (currentStatus === TicketStatus.CLOSED) return TicketStatus.CLOSED;
  if (issueReports.length === 0) return currentStatus;
  const allResolved = issueReports.every(ir => ir.status === IssueReportStatus.RESOLVED || ir.status === IssueReportStatus.REJECTED);
  const allPending = issueReports.every(ir => ir.status === IssueReportStatus.PENDING);
  if (allResolved) return TicketStatus.RESOLVED;
  if (allPending) return currentStatus === TicketStatus.NEW ? TicketStatus.NEW : TicketStatus.OPEN;
  return TicketStatus.IN_PROGRESS;
}
